import { createCtx } from '@mi-front/core';
import React, { ReactNode } from 'react';
import Async, { AsyncFulfilled } from 'react-async';
import { LoaderData, ViewError, ViewLoader } from './components';

export type AsyncChildProps<Data, Query = object> = AsyncFulfilled<Data> & {
  query: Query;
};

export interface AsyncViewProps<Data, Query = object> {
  promiseFn: (args: { query: Query }) => Promise<Data>;
  children: (p: AsyncChildProps<Data, Query>) => React.ReactElement;
  query?: Query;
  loader?: LoaderData;
  silent?: boolean;
  customLoader?: ReactNode;
}

export const [useAsyncViewCtx, AsyncViewCtxProvider] = createCtx<{
  reload: () => void;
}>();

/**
 * TODO: replace with react-query
 */
export function AsyncView<Data, Query = object>(viewProps: AsyncViewProps<Data, Query>) {
  const { children, customLoader, loader, promiseFn, query, silent } = viewProps;

  // react-async's PromiseFn typing does not match our `{ query }` arg shape.
  const asyncPromiseFn = promiseFn as unknown as (
    props: Record<string, unknown>,
    controller: AbortController,
  ) => Promise<Data>;

  return (
    <Async promiseFn={asyncPromiseFn} query={query}>
      {(p) => {
        if (p.isPending && (!silent || p.counter <= 1)) {
          return customLoader || <ViewLoader loaderData={loader} />;
        }

        if (p.error) {
          return <ViewError error={p.error} onRetry={p.reload} />;
        }

        if (typeof p.data !== 'undefined') {
          const childProps = {
            ...p,
            query: query || ({} as Query),
          } as AsyncChildProps<Data, Query>;

          return (
            <AsyncViewCtxProvider value={{ reload: p.reload }}>
              {children(childProps)}
            </AsyncViewCtxProvider>
          );
        }

        return null;
      }}
    </Async>
  );
}
